import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import DynamicSelect from "../DynamicSelect";
import PageHeading from "../PageHeading";

const API_URL = "https://api.muktihospital.com/api/contact-page";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const EMPTY = {
  metaTitle: "",
  metaDescription: "",
  locationTitle: "",
  locationText: "",
  emailTitle: "",
  emailAddress: "",
  phoneNumber: "",
  hoursTitle: "",
  hoursText: "",
  mapUrl: "",
};

const ContactPage = () => {
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allForms, setAllForms] = useState({ en: { ...EMPTY }, bn: { ...EMPTY } });

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  // Load both languages once (switching won't wipe unsaved edits)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(API_URL, { headers: { "x-api-key": API_KEY } });
        const translations = res.data?.translations || {};
        const pick = (obj = {}) => ({
          metaTitle: obj.metaTitle || "",
          metaDescription: obj.metaDescription || "",
          locationTitle: obj.locationTitle || "",
          locationText: obj.locationText || "",
          emailTitle: obj.emailTitle || "",
          emailAddress: obj.emailAddress || "",
          phoneNumber: obj.phoneNumber || "",
          hoursTitle: obj.hoursTitle || "",
          hoursText: obj.hoursText || "",
          mapUrl: obj.mapUrl || "",
        });
        setAllForms({ en: pick(translations.en), bn: pick(translations.bn) });
      } catch (err) {
        console.error("Error loading contact page:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const form = allForms[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAllForms((prev) => ({
      ...prev,
      [language]: { ...prev[language], [name]: value },
    }));
  };

  const handleLanguageChange = (opt) => setLanguage(opt.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        API_URL,
        { language, ...form },
        { headers: { "x-api-key": API_KEY, "Content-Type": "application/json" } }
      );
      toast.success(`Contact page saved (${language})`);
    } catch (err) {
      console.error("Error saving contact page:", err);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Sections" },
    { label: "Contact Page" },
  ];

  const field = (label, name, placeholder = "Leave empty to use default", isTextarea = false) => (
    <div>
      <label className="label">{label}</label>
      {isTextarea ? (
        <textarea name={name} rows={2} value={form[name]} onChange={handleChange} className="input-field" placeholder={placeholder} />
      ) : (
        <input type="text" name={name} value={form[name]} onChange={handleChange} className="input-field" placeholder={placeholder} />
      )}
    </div>
  );

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading title="Contact Page" breadcrumbs={breadcrumbs} />

      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((o) => o.value === language)}
          isSearchable
        />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg p-5 space-y-4">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed pb-2">
            SEO ({language === "en" ? "English" : "Bangla"})
          </h2>
          {field("Meta Title", "metaTitle")}
          {field("Meta Description", "metaDescription", "Leave empty to use default", true)}
        </div>

        <div className="bg-white shadow-sm rounded-lg p-5 space-y-4">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed pb-2">
            Contact Info ({language === "en" ? "English" : "Bangla"})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field("Location Title", "locationTitle", "e.g. Location")}
            {field("Location Text", "locationText", "e.g. Race Course, Cumilla")}
            {field("Email Title", "emailTitle", "e.g. Email Address")}
            {field("Email Address", "emailAddress", "e.g. info@muktihospital.com")}
            {field("Opening Hours Title", "hoursTitle", "e.g. Opening Hours")}
            {field("Opening Hours Text", "hoursText", "e.g. Saturday - Thursday, 09:00 AM - 05:00 PM", true)}
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg p-5 space-y-4">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed pb-2">
            Google Map
          </h2>
          {field("Map Embed URL", "mapUrl", "Paste the Google Maps embed src URL", true)}
          <p className="text-xs text-gray-400">
            Google Maps → Share → Embed a map → copy the URL inside src=&quot;...&quot;
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || loading}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactPage;